<template>
    <div>
        <header-message>Please Enter <span class="measured">Measured</span> Windshield Angle</header-message>
        <form>
            <div class="container-fluid">
                <form-row :label-width="8">
                    <template #label>
                        <form-label>Measured Windshield Angle</form-label>
                    </template>
                    <template #fields>
                        <decimal-keyboard v-model="angleText"
                                          css-class="form-control"
                                          :maxlength="4" placeholder="Angle"
                                          :auto-focus="true"
                                          :readonly="loading"
                                          @accept="accept" />
                    </template>
                </form-row>
            </div>
        </form>
        <div class="buttonrow">
            <button-button type="button" class="btn btn-secondary me-4" @click.prevent="cancelClicked" :disabled="loading">Cancel</button-button>
            <button-button type="button" class="btn btn-primary me-4" @click.prevent="nextClicked" :disabled="loading || !isValid">Next</button-button>
        </div>
    </div>
</template>

<style scoped lang="scss">
    .measured {
        color: orange;
    }
</style>

<script lang="ts">
import { defineComponent } from "vue";

import PageBase from "@/shared/components/common/PageBase";
import HeaderMessage from "@/components/HeaderMessage.vue";
import { Global } from "@/support/GlobalData";
import { SignalR } from "@/support/SignalR";
import { CalibrationEventSupport } from "@/support/CalibrationEvent";
import { CalibrationEvent } from "@/shared/enums/CalibrationEvent";
import { DeviceStatusDto } from "@/shared/models/DeviceStatusDto";
import { confirmYesNo } from "@/shared/components/common/AlertDialog.vue";
import { ButtonClicked } from "@/shared/enums/ButtonClicked";

const WINDSHIELD_ANGLE_TOLERANCE_DEGREES = 3;

export default defineComponent({
    name: "CalibrationNewWindshieldAngle",
    mixins: [PageBase],
    components: {
        HeaderMessage,
    },
    data() {
        return {
            angle: 0 as number
        };
    },
    computed: {
        angleText: {
            get(): string {
                if (!this.angle) return "";
                return this.angle.toFixed(1);
            },
            set(v: string) {
                try {
                    this.angle = Number(v);
                } catch (e: any) {
                    this.angle = 0;
                }
            }
        },
        isValid(): boolean {
            return this.angleText.match(/^[1-9][0-9]{0,1}(\.[0-9]){0,1}$/) != null;
        }
    },
    methods: {
        accept(): void {
            if (!this.isValid) return;
            this.nextClicked();
        },
        nextClicked(): void {
            if (!Global.OriginalWindshieldAngle
                || this.angle < Global.OriginalWindshieldAngle - WINDSHIELD_ANGLE_TOLERANCE_DEGREES
                || this.angle > Global.OriginalWindshieldAngle + WINDSHIELD_ANGLE_TOLERANCE_DEGREES) {
                Global.CalibrationSession!.WindshieldOverride = null; // clear override
                this.$router.push("/Calibration/ContactSupportWindshieldAngle");
            } else {
                this.loading = true;
                Global.CalibrationSession!.WindshieldOverride = this.angle;
                CalibrationEventSupport.sendEventCalibrationRestart(CalibrationEvent.RestartCalibration, this.angle);
            }
        },
        cancelClicked(): void {
            confirmYesNo("Are you sure you want to cancel this calibration?")
                .then((clicked: ButtonClicked): void => {
                    if (clicked === ButtonClicked.Ok) {
                        CalibrationEventSupport.sendEvent(CalibrationEvent.CalibrationCustomerCanceled);
                        Global.MainApp.clearSession();
                        this.$router.push("/");
                    }
                });
        },
        setWindshieldAngleByCompanion(angle: number): void {
            this.angle = angle;
            this.nextClicked();
        },
        getCurrentStatusForListeners(): void {
            CalibrationEventSupport.sendEventStatus({
                Page: "CalibrationNewWindshieldAngle",
            } as DeviceStatusDto);
        }
    },
    mounted(): void {
        SignalR.onGetCurrentStatusForListeners = this.getCurrentStatusForListeners;
        SignalR.onWindshieldAngleSelectionByCompanion = this.setWindshieldAngleByCompanion;
        this.getCurrentStatusForListeners();
    },
    unmounted(): void {
        SignalR.onGetCurrentStatusForListeners = null;
        SignalR.onWindshieldAngleSelectionByCompanion = null;
    }
});
</script>
